/* ============================================================
   Wiredan App - GlobalChatBanner.jsx
   Path: src/components/GlobalChatBanner.jsx
   Status: PRODUCTION READY | MEDIA HUB DIRECT LINK FIX
   ============================================================ */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiRadio, FiMessageSquare, FiZap, FiLock, FiUnlock 
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import AdminBroadcastModal from './AdminBroadcastModal';
import axios from 'axios';

export default function GlobalChatBanner({ externalOpen, onToggleExternal }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Dynamic user email extraction with secure master admin check
  const userEmail = (user?.email || user?.uuid || "user@wiredan.com").toLowerCase().trim();
  const isMasterAdmin = userEmail === 'mtlhumaid@gmail.com';

  // Sync external open triggers from parent headers
  useEffect(() => {
    if (typeof externalOpen === 'boolean') {
      setIsExpanded(externalOpen);
    }
  }, [externalOpen]);

  // Check subscription status to auto-unlock
  useEffect(() => {
    if (isMasterAdmin) {
      setIsUnlocked(true);
      return;
    }

    const checkSubscription = async () => {
      const savedExpiry = localStorage.getItem('wiredan_sub_expiry');
      if (savedExpiry && new Date(savedExpiry) > new Date()) {
        setIsUnlocked(true);
        return;
      }

      try {
        const res = await axios.get(`/api/vault/subscription-status?userEmail=${encodeURIComponent(userEmail)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.data?.success && res.data?.expiresAt && new Date(res.data.expiresAt) > new Date()) {
          setIsUnlocked(true);
          localStorage.setItem('wiredan_sub_expiry', res.data.expiresAt);
        } else {
          setIsUnlocked(false);
        }
      } catch (err) {
        setIsUnlocked(false);
      }
    };

    if (userEmail) checkSubscription();
  }, [userEmail, token, isMasterAdmin]);

  const handleToggle = () => {
    const nextState = !isExpanded;
    setIsExpanded(nextState);
    if (onToggleExternal) onToggleExternal(nextState);
  };

  // Opens Media Hub feed directly in a new tab instead of routing to studio
  const handleOpenMediaHub = (e) => {
    e.preventDefault();
    window.open('https://wiredan.com/rss.xml', '_blank', 'noopener,noreferrer');
  };

  const handleOpenStudioHub = () => {
    // Navigates directly to the unified Pro Studio & Viral Broadcast Hub Gateway
    navigate('/music-studio');
  };

  const handleOpenBroadcastModal = () => {
    if (!isUnlocked && !isMasterAdmin) {
      // If locked, redirect user to the subscription gateway page
      navigate('/music-studio');
      return;
    }
    setIsBroadcastModalOpen(true);
  };

  return (
    <>
      <div className="w-full bg-[#004D25] text-white p-3 sm:p-4 shadow-2xl font-sans rounded-b-[28px] border-b-4 border-emerald-500/40">
        
        <div className="max-w-7xl mx-auto space-y-3">
          
          {/* Header Title Section */}
          <div className="space-y-0.5">
            <h2 className="text-xs font-black uppercase tracking-wider text-emerald-200 flex items-center gap-1.5">
              <FiZap className="text-amber-400 animate-pulse" size={14} /> 
              WIREDAN GLOBAL INTELLIGENCE NODE
            </h2>
            <p className="text-[10px] text-slate-200 font-mono flex items-center gap-2">
              <span>Powered by Wiredan</span>
              <span className="text-emerald-400/50">•</span>
              <a 
                href="https://wiredan.com/rss.xml"
                onClick={handleOpenMediaHub}
                className="text-amber-300 underline font-extrabold cursor-pointer hover:text-white transition-colors flex items-center gap-1"
              >
                📡 Wiredan Media Hub
              </a>
            </p>
          </div>

          {/* Large Action Cards Section (Matches Provided UI Image Exactly) */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-4 pt-1">
            
            {/* 1. PRO STUDIO & HUB Card */}
            <button
              type="button"
              onClick={handleOpenStudioHub}
              className="bg-[#F25C05] hover:bg-[#d45004] text-white p-2.5 sm:p-3.5 rounded-2xl font-black text-center shadow-lg transition-all cursor-pointer border border-orange-400/40 flex flex-col items-center justify-center gap-1 leading-tight group"
            >
              <div className="flex items-center gap-1 text-[11px] sm:text-xs uppercase tracking-wider font-extrabold">
                <FiRadio size={13} className="shrink-0" />
                <span>PRO STUDIO & HUB</span>
                {isUnlocked ? (
                  <FiUnlock size={11} className="text-emerald-300 shrink-0" />
                ) : (
                  <FiLock size={11} className="text-amber-200 shrink-0" />
                )}
              </div>
            </button>

            {/* 2. ROOT ADMIN PRO ACTIVE / BROADCAST HUB Card */}
            <button
              type="button"
              onClick={handleOpenBroadcastModal}
              className="bg-[#FF9F0A] hover:bg-amber-500 text-slate-950 p-2.5 sm:p-3.5 rounded-2xl font-black text-center shadow-lg transition-all cursor-pointer border border-amber-300/60 flex flex-col items-center justify-center gap-1 leading-tight"
            >
              <div className="flex items-center gap-1 text-[11px] sm:text-xs uppercase tracking-wider font-extrabold">
                <FiZap size={13} className="shrink-0" />
                <span>ROOT ADMIN PRO ACTIVE</span>
                {!isUnlocked && <FiLock size={11} className="text-slate-900 shrink-0" />}
              </div>
            </button>

            {/* 3. CHAT NODE Card */}
            <button
              type="button"
              onClick={handleToggle}
              className="bg-[#F25C05] hover:bg-[#d45004] text-white p-2.5 sm:p-3.5 rounded-2xl font-black text-center shadow-lg transition-all cursor-pointer border border-orange-400/40 flex flex-col items-center justify-center gap-1 leading-tight"
            >
              <div className="flex items-center gap-1 text-[11px] sm:text-xs uppercase tracking-wider font-extrabold">
                <FiMessageSquare size={13} className="shrink-0" />
                <span>CHAT NODE</span>
              </div>
            </button>

          </div>

        </div>

      </div>

      {/* Admin Viral Broadcast Hub Modal */}
      <AdminBroadcastModal 
        isOpen={isBroadcastModalOpen} 
        onClose={() => setIsBroadcastModalOpen(false)} 
        user={user} 
        token={token} 
      />
    </>
  );
}